<template>
    <div class="zoho-forward-settings">
        <div class="mode-card">
            <div class="title">
                <div class="title-content">
                    <ZohoIcon class="zoho-icon" />
                    <div>
                        <div class="title-text">{{ $t("message.autoTransfer") }}</div>
                        <div class="subtitle">
                            {{ $t("message.autoTransferDescription") }}
                        </div>
                    </div>
                </div>
                <a-switch
                    class="md-warning ml-3"
                    checked-children="✔"
                    un-checked-children="✖"
                    :checked="isZohoForwardEnabled()"
                    @change="toggleZohoForward"
                />
            </div>
            <div class="settings-content" v-if="isZohoForwardEnabled()">
                <a-divider />
                
                <div class="apply-to-channels-section">
                    <a-button type="dashed" icon="copy" @click="showApplyToChannelsModal = true">
                        Bu Ayarları Diğer Kanallara Uygula
                    </a-button>
                </div>

                <a-divider />
                
                <a-collapse v-model="activeAccordionKeys" :bordered="false" class="zoho-settings-accordion">
                    <a-collapse-panel key="phoneFormat" header="Telefon Numarası Formatı">
                        <div class="setting-section">
                            <a-radio-group v-model="cloneChannel.zohoForward.phoneFormat" @change="updateClone" class="phone-format-radios">
                                <a-radio value="E164_WITHOUT_PLUS">E.164 (+'sız) - Varsayılan (905xxxxxxxxx)</a-radio>
                                <a-radio value="E164_WITH_PLUS">E.164 (+'lı) (+905xxxxxxxxx)</a-radio>
                            </a-radio-group>
                        </div>
                    </a-collapse-panel>

                    <a-collapse-panel key="leadWizard" header="Lead Oluşturma Sihirbazı">
                        <div class="setting-section">
                            <div class="section-title">Lead Oluşturma Sihirbazı</div>
                    <div class="section-desc">
                        Zoho'da Lead oluşturulurken hangi alanların hangi değerlerle doldurulacağını buradan
                        ayarlayabilirsiniz. Değer kısmına statik metin yazabilir veya
                        <code v-pre>&#123;&#123; contact.name &#125;&#125;</code> gibi dinamik değişkenler kullanabilirsiniz.
                    </div>

                    <div class="variable-helpers">
                        <span class="helper-label">Kullanılabilir Değişkenler:</span>
                        <div class="tags">
                            <a-tag v-for="v in availableVariables" :key="v.key" @click="copyVariable(v.key)">
                                {{ v.label }}
                            </a-tag>
                        </div>
                    </div>

                    <div class="mapped-fields">
                        <div v-for="(value, key) in leadFields" :key="key" class="field-item">
                            <div class="field-header">
                                <span class="field-label"
                                    >{{ getFieldLabel(key) }} <small>({{ key }})</small></span
                                >
                                <a-button
                                    type="link"
                                    icon="delete"
                                    size="small"
                                    @click="removeField(key)"
                                    class="delete-btn"
                                />
                            </div>
                            
                            <!-- Picklist Field with Static/Dynamic Toggle -->
                            <div v-if="isPicklistField(key)" class="picklist-field-wrapper">
                                <div class="field-mode-toggle">
                                    <a-radio-group 
                                        
                                        :value="fieldModes[key]"
                                        
                                        
                                        size="small" 
                                        button-style="solid"
                                        @change="onFieldModeChange(key)"
                                    >
                                        <a-radio-button value="static" @click="setFieldMode(key, 'static')">Statik</a-radio-button>
                                        <a-radio-button value="dynamic" @click="setFieldMode(key, 'dynamic')">Dinamik</a-radio-button>
                                    </a-radio-group>
                                </div>
                                
                                <!-- Static Mode: Show Picker -->
                                <div v-if="fieldModes[key] === 'static'" class="static-mode">
                                    <CustomPicker
                                        v-model="cloneChannel.zohoForward.leadFields[key]"
                                        :items="getPicklistValues(key)"
                                        idKey="actual_value"
                                        labelKey="display_value"
                                        :title="'Değer Seçin'"
                                        @input="updateClone"
                                    />
                                </div>
                                
                                <!-- Dynamic Mode: Show Input -->
                                <div v-else class="dynamic-mode">
                                    <a-popover
                                        v-model="fieldPopoverVisible[key]"
                                        trigger="manual"
                                        placement="bottomLeft"
                                        overlayClassName="variable-suggestions-popover"
                                        :getPopupContainer="(trigger) => trigger.parentNode"
                                    >
                                        <template #content>
                                            <div class="variable-suggestions" :ref="'popover_dynamic_' + key">
                                                <div class="suggestion-search">
                                                    <a-input-search
                                                        v-model="variableSearchQuery"
                                                        placeholder="Değişken ara..."
                                                        size="small"
                                                        @keydown.esc="closePopover(key)"
                                                    />
                                                </div>
                                                <div class="suggestion-list">
                                                    <div
                                                        v-for="v in getFilteredVariables()"
                                                        :key="v.key"
                                                        class="suggestion-item"
                                                        @click="insertVariable(key, v.key)"
                                                    >
                                                        <span class="var-label">{{ v.label }}</span>
                                                        <span class="var-key">{{ formatVariable(v.key) }}</span>
                                                    </div>
                                                    <div v-if="getFilteredVariables().length === 0" class="no-results">
                                                        Sonuç bulunamadı
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <a-input
                                            :ref="'input_dynamic_' + key"
                                            v-model="cloneChannel.zohoForward.leadFields[key]"
                                            @change="updateClone"
                                            @keyup="handleInputKeyup($event, key)"
                                            @keydown.esc="closePopover(key)"
                                            @blur="handleInputBlur(key)"
                                            placeholder="Değer giriniz... ({ karakteri ile değişken ekleyebilirsiniz)"
                                        />
                                    </a-popover>
                                </div>
                            </div>
                            
                            <!-- Regular Text Field -->
                            <a-popover
                                v-else
                                v-model="fieldPopoverVisible[key]"
                                trigger="manual"
                                placement="bottomLeft"
                                overlayClassName="variable-suggestions-popover"
                                :getPopupContainer="(trigger) => trigger.parentNode"
                            >
                                <template #content>
                                    <div class="variable-suggestions" :ref="'popover_' + key">
                                        <div class="suggestion-search">
                                            <a-input-search
                                                v-model="variableSearchQuery"
                                                placeholder="Değişken ara..."
                                                size="small"
                                                @keydown.esc="closePopover(key)"
                                            />
                                        </div>
                                        <div class="suggestion-list">
                                            <div
                                                v-for="v in getFilteredVariables()"
                                                :key="v.key"
                                                class="suggestion-item"
                                                @click="insertVariable(key, v.key)"
                                            >
                                                <span class="var-label">{{ v.label }}</span>
                                                <span class="var-key">{{ formatVariable(v.key) }}</span>
                                            </div>
                                            <div v-if="getFilteredVariables().length === 0" class="no-results">
                                                Sonuç bulunamadı
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <a-input
                                    :ref="'input_' + key"
                                    v-model="cloneChannel.zohoForward.leadFields[key]"
                                    @change="updateClone"
                                    @keyup="handleInputKeyup($event, key)"
                                    @keydown.esc="closePopover(key)"
                                    @blur="handleInputBlur(key)"
                                    placeholder="Değer giriniz... ({ karakteri ile değişken ekleyebilirsiniz)"
                                />
                            </a-popover>
                        </div>
                    </div>

                    <div class="add-field-wrapper">
                        <CustomPicker
                            v-model="selectedFieldToAdd"
                            :items="availableZohoFields"
                            idKey="api_name"
                            labelKey="field_label"
                            title="Alan Ekle"
                            icon="add"
                        />
                        <SupsisButton primary @click="addField(selectedFieldToAdd)" :disabled="!selectedFieldToAdd">
                            <i class="material-icons">add</i>
                            <span>Ekle</span>
                        </SupsisButton>
                    </div>
                        </div>
                    </a-collapse-panel>
                </a-collapse>
            </div>
        </div>

        <!-- Apply Settings to Other Channels Modal -->
        <a-modal
            v-model="showApplyToChannelsModal"
            title="Ayarları Diğer Kanallara Uygula"
            @ok="applySettingsToChannels"
            okText="Uygula"
            cancelText="İptal"
            width="600px"
        >
            <div class="apply-channels-content">
                <a-alert
                    message="Dikkat"
                    description="Seçtiğiniz kanallardaki mevcut Zoho ayarları değiştirilecektir. Bu işlem geri alınamaz."
                    type="warning"
                    show-icon
                    style="margin-bottom: 15px;"
                />
                
                <div class="channel-selection-info">
                    <strong>Uygulanacak Ayarlar:</strong>
                    <ul>
                        <li>Telefon Numarası Formatı: {{ getPhoneFormatLabel() }}</li>
                        <li>Lead Alanları: {{ Object.keys($_.get(cloneChannel, "zohoForward.leadFields", {}) || {}).length }} alan</li>
                    </ul>
                </div>

                <a-divider />

                <div class="channels-list">
                    <div class="list-header">
                        <a-checkbox
                            :indeterminate="selectedChannelIds.length > 0 && selectedChannelIds.length < availableChannelsForCopy.length"
                            :checked="selectedChannelIds.length === availableChannelsForCopy.length && availableChannelsForCopy.length > 0"
                            @change="onSelectAllChannels"
                        >
                            Tümünü Seç
                        </a-checkbox>
                    </div>
                    <div class="channel-items">
                        <a-checkbox-group v-model="selectedChannelIds">
                            <div v-for="ch in availableChannelsForCopy" :key="ch._id" class="channel-item">
                                <a-checkbox :value="ch._id">
                                    <span class="channel-name">{{ ch.name }}</span>
                                    <a-tag :color="getChannelTypeColor(ch.type)" size="small">{{ ch.type }}</a-tag>
                                </a-checkbox>
                            </div>
                        </a-checkbox-group>
                        <div v-if="availableChannelsForCopy.length === 0" class="no-channels">
                            Ayarları kopyalayabileceğiniz başka kanal bulunmamaktadır.
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import ZohoIcon from "../Chatbot/pages/integrations/svg/ZohoIcon.vue";
import { mapState, mapActions } from "vuex";
import _ from "lodash";

import { ZOHO_API } from "../../../api/zoho";
import CustomPicker from "../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "ZohoForwardSettings",
    components: {
        ZohoIcon,
        CustomPicker,
        SupsisButton,
    },
    props: {
        channel: {
            type: Object,
            default: () => ({}),
            required: true,
        },
    },
    data: function () {
        return {
            cloneChannel: _.cloneDeep(this.channel),
            zohoFields: [],
            selectedFieldToAdd: null,
            fieldPopoverVisible: {},
            variableSearchQuery: "",
            cursorPositions: {},
            showApplyToChannelsModal: false,
            selectedChannelIds: [],
            activeAccordionKeys: ["phoneFormat", "leadWizard"],
            fieldModes: {},
            availableVariables: [
                { key: "contact._id", label: "Kişi ID" },
                { key: "contact.name", label: "Kişi Adı" },
                { key: "contact.fullname", label: "Tam Ad" },
                { key: "contact.phone", label: "Telefon" },
                { key: "contact.whatsappNumber", label: "WhatsApp Numarası" },
                { key: "contact.email", label: "E-posta" },
                { key: "contact.platform", label: "Platform" },
                { key: "contact.channel.type", label: "Kanal Tipi" },
                { key: "contact.channel.name", label: "Kanal Adı" },
                { key: "contact.city", label: "Şehir" },
                { key: "contact.country", label: "Ülke" },
                { key: "contact.visitingPage", label: "Ziyaret Edilen Sayfa" },
                { key: "contact.referer", label: "Yönlendiren" },
                { key: "contact.ipAddress", label: "IP Adresi" },
                { key: "contact.browser", label: "Tarayıcı" },
                { key: "contact.os", label: "İşletim Sistemi" },
                { key: "contact.instagramUserId", label: "Instagram ID" },
                { key: "contact.messengerUserId", label: "Messenger ID" },
                { key: "contact.telegramUserId", label: "Telegram ID" },
                { key: "contact.linkedinUserId", label: "LinkedIn ID" },
                { key: "contact.preferredLang", label: "Tercih Edilen Dil" },
                { key: "contact.vipCustomer", label: "VIP Müşteri" },
                { key: "contact.owner", label: "Sahip" },
                { key: "contact.tags", label: "Etiketler" },
                { key: "contact.metaReferrals[0].headline", label: "Reklam Başlığı" },
                { key: "contact.metaReferrals[0].adInfo.name", label: "Reklam Adı" },
                { key: "contact.metaReferrals[0].adInfo.adset.name", label: "Reklam Seti Adı" },
                { key: "contact.metaReferrals[0].adInfo.campaign.name", label: "Kampanya Adı" },
                { key: "contact.metaLeadInfo.ad_name", label: "Meta Lead Reklam Adı" },
                { key: "contact.metaLeadInfo.campaign_name", label: "Meta Lead Kampanya" },
                { key: "contact.metaLeadInfo.form_name", label: "Meta Lead Form Adı" },
                { key: "now", label: "Şu Anki Tarih (ISO)" },
            ],
        };
    },
    computed: {
        ...mapState({
            zohoChannels: (state) => state.channel.channels.filter((channel) => channel.type === "zoho"),
            allChannels: (state) => state.channel.channels,
        }),
        leadFields() {
            return this.cloneChannel.zohoForward?.leadFields || {};
        },
        availableZohoFields() {
            const usedKeys = Object.keys(this.leadFields);
            return this.zohoFields.filter((f) => !usedKeys.includes(f.api_name));
        },
        availableChannelsForCopy() {
            return this.allChannels.filter((ch) => {
                // Exclude current channel and deleted channels
                return ch._id !== this.channel._id && !ch.isDeleted;
            });
        },
    },
    methods: {
        ...mapActions({
            updateChannelAction: "channel/updateChannel",
        }),
        isZohoForwardEnabled() {
            return !!this.cloneChannel.zohoForward?.enabled;
        },
        getPhoneFormatLabel() {
            const format = this.cloneChannel.zohoForward?.phoneFormat;
            if (format === "E164_WITH_PLUS") return "E.164 (+'lı)";
            return "E.164 (+'sız)";
        },
        getChannelTypeColor(type) {
            const colors = {
                "whatsapp-business-api": "green",
                "whatsapp-v2": "green",
                "whatsapp-qr": "green",
                "instagram": "purple",
                "messenger": "blue",
                "telegram": "cyan",
                "webchat": "orange",
            };
            return colors[type] || "default";
        },
        onSelectAllChannels(e) {
            if (e.target.checked) {
                this.selectedChannelIds = this.availableChannelsForCopy.map((ch) => ch._id);
            } else {
                this.selectedChannelIds = [];
            }
        },
        async applySettingsToChannels() {
            if (this.selectedChannelIds.length === 0) {
                this.$message.warning("Lütfen en az bir kanal seçin");
                return;
            }

            const zohoSettings = {
                enabled: this.cloneChannel.zohoForward?.enabled || false,
                phoneFormat: this.cloneChannel.zohoForward?.phoneFormat || "E164_WITHOUT_PLUS",
                leadFields: _.cloneDeep(this.cloneChannel.zohoForward?.leadFields || {}),
            };

            try {
                const promises = this.selectedChannelIds.map((channelId) => {
                    const channel = this.allChannels.find((ch) => ch._id === channelId);
                    if (!channel) return Promise.resolve();

                    const updatedChannel = Object.assign({}, channel, {
                        zohoForward: zohoSettings,
                    });

                    return this.updateChannelAction(updatedChannel);
                });

                await Promise.all(promises);
                
                this.$message.success(`${this.selectedChannelIds.length} kanala ayarlar başarıyla uygulandı`);
                this.showApplyToChannelsModal = false;
                this.selectedChannelIds = [];
            } catch (error) {
                console.error("applySettingsToChannels error", error);
                this.$message.error("Ayarlar uygulanırken bir hata oluştu");
            }
        },
        async toggleZohoForward(checked) {
            if (checked && this.zohoChannels.length === 0) {
                this.$confirm({
                    title: this.$t("message.zohoChannelRequiredTitle"),
                    content: this.$t("message.zohoChannelRequiredContent"),
                    okText: this.$t("message.okText"),
                    cancelText: this.$t("message.cancelText"),
                    onOk: () => {
                        this.$router.push("/integrations/zoho");
                    },
                });
                return;
            }

            const zohoForward = this.cloneChannel.zohoForward || {};
            this.$set(this.channel, "zohoForward", {
                ...zohoForward,
                enabled: checked,
            });
            // cloneChannel will update via watcher
        },
        async fetchZohoFields() {
            try {
                const fields = await ZOHO_API.listModuleFields("Leads");
                if (Array.isArray(fields)) {
                    this.zohoFields = fields;
                }
            } catch (e) {
                console.error("Zoho Fields fetch error", e);
            }
        },
        getFieldLabel(key) {
            const field = this.zohoFields.find((f) => f.api_name === key);
            return field ? field.field_label : key;
        },
        isPicklistField(key) {
            const field = this.zohoFields.find((f) => f.api_name === key);
            return field && field.data_type === "picklist" && field.pick_list_values && field.pick_list_values.length > 0;
        },
        getPicklistValues(key) {
            const field = this.zohoFields.find((f) => f.api_name === key);
            return field && field.pick_list_values ? field.pick_list_values : [];
        },
        onFieldModeChange(key) {
            // When switching modes, clear the current value
            this.$set(this.cloneChannel.zohoForward.leadFields, key, "");
            this.updateClone();
        },
        setFieldMode(key, mode) {
            console.log("setFieldMode", {key, mode});
            this.$set(this.fieldModes, key, mode);
            this.fieldModes = _.cloneDeep(this.fieldModes);
            this.updateClone();
        },
        initializeFieldModes() {
            // Initialize field modes based on current values
            const leadFields = this.cloneChannel.zohoForward?.leadFields || {};
            Object.keys(leadFields).forEach((key) => {
                if (this.isPicklistField(key)) {
                    const value = leadFields[key];
                    // If value contains {{, it's dynamic, otherwise static
                    const isDynamic = typeof value === "string" && value.includes("{{");
                    this.$set(this.fieldModes, key, isDynamic ? "dynamic" : "static");
                }
            });
        },
        addField(key) {
            if (!key) return;
            if (!this.cloneChannel.zohoForward) {
                this.$set(this.cloneChannel, "zohoForward", {
                    enabled: false,
                    phoneFormat: "E164_WITHOUT_PLUS",
                    leadFields: {}
                });
            }
            if (!this.cloneChannel.zohoForward.leadFields) {
                this.$set(this.cloneChannel.zohoForward, "leadFields", {});
            }
            const field = this.zohoFields.find((f) => f.api_name === key);
            let defaultValue = "";
            
            // Initialize field mode for picklist fields
            if (this.isPicklistField(key)) {
                this.$set(this.fieldModes, key, "static");
            }
            
            // Otomatik öneriler
            if (field && field.api_name === "Lead_Source") {
                defaultValue = "Supsis/{{contact.channel.type}}/{{contact.channel.name}}";
                if (this.isPicklistField(key)) {
                    this.$set(this.fieldModes, key, "dynamic");
                }
            } else if (field && field.field_label.toLowerCase().includes("phone")) {
                defaultValue = "{{contact.phone}}";
                if (this.isPicklistField(key)) {
                    this.$set(this.fieldModes, key, "dynamic");
                }
            } else if (field && field.field_label.toLowerCase().includes("email")) {
                defaultValue = "{{contact.email}}";
                if (this.isPicklistField(key)) {
                    this.$set(this.fieldModes, key, "dynamic");
                }
            }
            
            this.$set(this.cloneChannel.zohoForward.leadFields, key, defaultValue);
            this.selectedFieldToAdd = null;
            this.updateClone();
        },
        removeField(key) {
            this.$delete(this.cloneChannel.zohoForward.leadFields, key);
            this.updateClone();
        },
        updateClone() {
            // Trigger watcher by creating a deep clone of itself effectively,
            // but actually we just need to ensure the parent gets the update.
            // The watcher on cloneChannel will handle it.
            // However, v-model mutation on cloneChannel properties might not trigger deep watch if not set correctly?
            // Vue 2 deep watch works.
            // But let's force update if needed.
            this.cloneChannel = _.cloneDeep(this.cloneChannel);
        },
        copyVariable(variable) {
            const text = "{{" + variable + "}}";
            navigator.clipboard.writeText(text).then(() => {
                this.$message.success("Kopyalandı: " + text);
            });
        },
        formatVariable(v) {
            return "{{" + v + "}}";
        },
        getFilteredVariables() {
            const query = (this.variableSearchQuery || "").toLowerCase();
            if (!query) return this.availableVariables;
            return this.availableVariables.filter((v) => {
                return v.label.toLowerCase().includes(query) || v.key.toLowerCase().includes(query);
            });
        },
        handleInputKeyup(event, fieldKey) {
            const input = event.target;
            const cursorPosition = input.selectionStart;
            const value = this.cloneChannel.zohoForward.leadFields[fieldKey] || "";
            
            // Check if user typed "{"
            const char = event.key;
            
            if (char === "{") {
                const textBeforeCursor = value.substring(0, cursorPosition);
                // Check if the last character before cursor is "{"
                if (textBeforeCursor.endsWith("{{")) {
                    // Store the position where the first "{" is
                    this.$set(this.cursorPositions, fieldKey, cursorPosition - 2);
                    this.$set(this.fieldPopoverVisible, fieldKey, true);
                    this.variableSearchQuery = "";
                }
            }
        },
        insertVariable(fieldKey, variableKey) {
            const currentValue = this.cloneChannel.zohoForward.leadFields[fieldKey] || "";
            const cursorPos = this.cursorPositions[fieldKey];
            const formattedVar = this.formatVariable(variableKey);
            
            if (cursorPos !== undefined) {
                // Remove the "{{" characters that triggered the popup and insert the variable
                const beforeCursor = currentValue.substring(0, cursorPos);
                // Skip the 2 characters "{{"
                const afterCursor = currentValue.substring(cursorPos + 2);
                
                const newValue = beforeCursor + formattedVar + afterCursor;
                
                this.$set(this.cloneChannel.zohoForward.leadFields, fieldKey, newValue);
            } else {
                // Fallback: just append
                this.$set(this.cloneChannel.zohoForward.leadFields, fieldKey, currentValue + formattedVar);
            }
            
            this.$set(this.fieldPopoverVisible, fieldKey, false);
            this.variableSearchQuery = "";
            delete this.cursorPositions[fieldKey];
            this.updateClone();
            
            // Focus back to input - try both ref names (regular and dynamic)
            this.$nextTick(() => {
                let inputRef = this.$refs["input_" + fieldKey];
                if (!inputRef) {
                    inputRef = this.$refs["input_dynamic_" + fieldKey];
                }
                
                if (inputRef) {
                    try {
                        let inputElement = null;
                        
                        // Try different ways to get the input element
                        if (inputRef && typeof inputRef === "object") {
                            // Array of refs (Vue sometimes returns array)
                            if (Array.isArray(inputRef)) {
                                inputRef = inputRef[0];
                            }
                            
                            // Ant Design Input component has $el
                            if (inputRef.$el) {
                                if (typeof inputRef.$el.querySelector === "function") {
                                    inputElement = inputRef.$el.querySelector("input");
                                } else if (inputRef.$el.tagName === "INPUT") {
                                    inputElement = inputRef.$el;
                                }
                            } 
                            // Try $refs.input
                            else if (inputRef.$refs && inputRef.$refs.input) {
                                inputElement = inputRef.$refs.input;
                            }
                            // Direct HTML element
                            else if (inputRef.tagName === "INPUT") {
                                inputElement = inputRef;
                            }
                        }
                        
                        if (inputElement && typeof inputElement.focus === "function") {
                            inputElement.focus();
                            // Set cursor position after the inserted variable
                            if (cursorPos !== undefined && typeof inputElement.setSelectionRange === "function") {
                                const newCursorPos = cursorPos + formattedVar.length;
                                inputElement.setSelectionRange(newCursorPos, newCursorPos);
                            }
                        }
                    } catch (e) {
                        console.warn("Could not focus input:", e);
                    }
                }
            });
        },
        closePopover(fieldKey) {
            this.$set(this.fieldPopoverVisible, fieldKey, false);
            this.variableSearchQuery = "";
        },
        handleInputBlur(fieldKey) {
            // Delay to allow click on suggestion
            setTimeout(() => {
                this.$set(this.fieldPopoverVisible, fieldKey, false);
                this.variableSearchQuery = "";
            }, 200);
        },
    },
    watch: {
        channel: {
            handler: function (newVal) {
                const isEqual = _.isEqual(newVal, this.cloneChannel);
                if (!isEqual) {
                    this.cloneChannel = _.cloneDeep(newVal);
                }
            },
            deep: true,
        },
        cloneChannel: {
            handler: function (newVal) {
                const isEqual = _.isEqual(newVal, this.channel);
                if (!isEqual) {
                    this.$emit("input", newVal);
                }
            },
            deep: true,
        },
        "cloneChannel.zohoForward.enabled": {
            handler(val) {
                if (val) {
                    this.fetchZohoFields();
                }
            },
            immediate: true,
        },
      
        selectedFieldToAdd: {
            handler(val) {
                if (val) {
                    this.addField(val);
                }
            },
        },
    },
    created() {
        if (this.isZohoForwardEnabled()) {
            this.fetchZohoFields();
        }
        this.initializeFieldModes();
    },
});
</script>

<style scoped lang="scss">
.zoho-forward-settings {
    margin-top: 20px;

    .mode-card {
        width: 100%;
        padding: 20px;
        border-radius: 5px;
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
        background-color: #fff;

        .title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 10px;

            .title-content {
                display: flex;
                align-items: center;

                .zoho-icon {
                    width: 100px;
                    margin-right: 12px;
                }

                .title-text {
                    font-size: 18px;
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                }

                .subtitle {
                    font-size: 14px;
                    color: var(--color-text-strong-default);
                }
            }
        }
    }

    .settings-content {
        margin-top: 10px;
    }

    .setting-section {
        margin-bottom: 20px;

        .section-title {
            font-weight: 600;
            margin-bottom: 10px;
            font-size: 15px;
        }

        .section-desc {
            font-size: 13px;
            color: #666;
            margin-bottom: 10px;
            line-height: 1.5;
        }
    }

    .variable-helpers {
        margin-bottom: 15px;
        padding: 10px;
        background: #f5f5f5;
        border-radius: 4px;

        .helper-label {
            font-size: 12px;
            font-weight: 600;
            display: block;
            margin-bottom: 5px;
        }

        .tags {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;

            .ant-tag {
                cursor: pointer;
                &:hover {
                    background: #e6f7ff;
                    border-color: #91d5ff;
                }
            }
        }
    }

    .mapped-fields {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 15px;

            .field-item {
                border: 1px solid #eee;
                padding: 10px;
                border-radius: 4px;
                background: #fff;

                .field-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 5px;

                    .field-label {
                        font-weight: 500;
                        font-size: 13px;
                        small {
                            color: #999;
                            font-weight: 400;
                        }
                    }

                    .delete-btn {
                        color: #ff4d4f;
                        &:hover {
                            color: #cf1322;
                        }
                    }
                }

                .picklist-field-wrapper {
                    .field-mode-toggle {
                        margin-bottom: 10px;
                    }

                    .static-mode,
                    .dynamic-mode {
                        width: 100%;
                    }
                }
            }
    }

    .add-field-wrapper {
        display: flex;
        justify-content: flex-start;
        gap: 10px;
        align-items: center;
    }

    .phone-format-radios {
        display: flex;
        flex-direction: row;
        gap: 20px;
        flex-wrap: wrap;
    }

    .apply-to-channels-section {
        margin: 15px 0;
        text-align: center;
    }

    .zoho-settings-accordion {
        background: transparent;

        ::v-deep .ant-collapse-item {
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            margin-bottom: 10px;
            background: #fff;

            .ant-collapse-header {
                font-weight: 600;
                font-size: 14px;
                background: #fafafa;
                border-radius: 4px 4px 0 0;
            }

            &.ant-collapse-item-active {
                .ant-collapse-header {
                    background: #e6f7ff;
                }
            }
        }
    }
}

.apply-channels-content {
    .channel-selection-info {
        padding: 10px;
        background: #f5f5f5;
        border-radius: 4px;
        margin-bottom: 15px;

        ul {
            margin: 10px 0 0 0;
            padding-left: 20px;
        }
    }

    .channels-list {
        .list-header {
            padding: 10px;
            background: #fafafa;
            border-radius: 4px 4px 0 0;
            border: 1px solid #e8e8e8;
            border-bottom: none;
        }

        .channel-items {
            border: 1px solid #e8e8e8;
            border-radius: 0 0 4px 4px;
            max-height: 300px;
            overflow-y: auto;
            padding: 10px;

            .channel-item {
                padding: 8px;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                    border-bottom: none;
                }

                .channel-name {
                    margin-right: 8px;
                }
            }

            .no-channels {
                padding: 20px;
                text-align: center;
                color: #999;
            }
        }
    }
}

.variable-suggestions-popover {
    .ant-popover-inner-content {
        padding: 0;
    }

    .variable-suggestions {
        min-width: 350px;
        max-width: 400px;
        display: flex;
        flex-direction: column;

        .suggestion-search {
            padding: 8px;
            background: #fafafa;
            border-bottom: 1px solid #eee;
        }

        .suggestion-list {
            max-height: 250px;
            overflow-y: auto;
        }

        .suggestion-item {
            padding: 10px 12px;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #f5f5f5;
            transition: background 0.2s;

            &:hover {
                background: #e6f7ff;
            }

            .var-label {
                font-size: 13px;
                color: #333;
                font-weight: 500;
            }

            .var-key {
                font-size: 11px;
                color: #999;
                font-family: monospace;
                margin-left: 10px;
            }
        }

        .no-results {
            padding: 20px;
            text-align: center;
            color: #999;
            font-size: 13px;
        }
    }
}
</style>
